import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { CheckCircle, EventBusy } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { bookingStatusConfig } from '../utils/bookingStatus';

/**
 * The cancel side of the pack page's bulk-booking dialog: one member's
 * bookings paid from THIS pack. Only BOOKED ones can be ticked; each runs the
 * normal admin cancel, so its class goes back to the pack. Nothing is deleted.
 *
 * Nothing is ticked to start. The confirm shows the server's dry run for
 * exactly the ticked ids.
 */

interface PackBooking {
  _id: string;
  bookingId: string | null;
  bookingStatus: string;
  date: string | null;
  istDate: string | null;
  istTime: string | null;
  title: string;
  canCancel: boolean;
}

type CancelResult = Awaited<ReturnType<typeof apiService.cancelBulkPackBookings>>;

interface Props {
  purchasedPackId: string;
  studioId?: string;
  /** Called after something was cancelled, so the page can refresh credits. */
  onCancelled: () => void;
}

const fmtDay = (key: string) =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtMonth = (ym: string) =>
  new Date(`${ym}-01T00:00:00Z`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const fmtTime = (hm: string) => {
  const [h, m] = hm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};
const errMessage = (e: any, fallback: string) => {
  const m = e?.message ?? e?.data?.message;
  return Array.isArray(m) ? m.join(', ') : m || fallback;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
// The panel's shared status colours (same source as BookingStatusPill), as a
// small tinted tag — the full pill is too heavy for a dense table.
const darken = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) => Math.round(((n >> shift) & 255) * 0.7).toString(16).padStart(2, '0');
  return `#${ch(16)}${ch(8)}${ch(0)}`;
};
const StatusTag: React.FC<{ status: string }> = ({ status }) => {
  const { label, color } = bookingStatusConfig(status);
  return (
    <Box component="span" sx={{
      display: 'inline-block', fontSize: 12, fontWeight: 600, lineHeight: 1.6, px: 1, borderRadius: 1,
      bgcolor: `${color}1F`, color: darken(color), whiteSpace: 'nowrap',
    }}>
      {label}
    </Box>
  );
};

const PackBookingsCancelPane: React.FC<Props> = ({ purchasedPackId, studioId, onCancelled }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<PackBooking[]>([]);
  const [show, setShow] = useState<'upcoming' | 'all'>('upcoming');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dry, setDry] = useState<CancelResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<CancelResult | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiService
      .getBulkPackBookings(purchasedPackId, studioId)
      .then((res: any) => setBookings(res.bookings ?? []))
      .catch((e: any) => setError(errMessage(e, 'Could not load this pack’s bookings.')))
      .finally(() => setLoading(false));
  }, [purchasedPackId, studioId]);

  useEffect(() => {
    setSelected(new Set());
    setDry(null);
    setDone(null);
    load();
  }, [load]);

  // Upcoming = the class hasn't started yet (this morning's class is past).
  const visible = useMemo(() => {
    if (show === 'all') return bookings;
    const now = Date.now();
    return bookings.filter((b) => !!b.date && new Date(b.date).getTime() > now);
  }, [bookings, show]);
  const byMonth = useMemo(() => {
    const m = new Map<string, PackBooking[]>();
    for (const b of visible) {
      const k = (b.istDate ?? '').slice(0, 7) || 'unknown';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(b);
    }
    return m;
  }, [visible]);

  // Only what is on screen can be acted on.
  const picked = visible.filter((b) => selected.has(b._id));

  // A dry run belongs to the exact selection it was computed for.
  useEffect(() => { setDry(null); }, [selected, show]);

  const toggle = (id: string) =>
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const setMany = (rows: PackBooking[], on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      rows.filter((b) => b.canCancel).forEach((b) => (on ? n.add(b._id) : n.delete(b._id)));
      return n;
    });

  const run = async (dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.cancelBulkPackBookings({
        purchasedPackId,
        bookingIds: picked.map((b) => b._id),
        dryRun,
        studioId,
      });
      if (dryRun) {
        setDry(res);
      } else {
        setDone(res);
        setSelected(new Set());
        if (res.cancelledCount) onCancelled();
        load();
      }
    } catch (e: any) {
      setError(errMessage(e, dryRun ? 'Could not check what would be cancelled.' : 'Cancel failed.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !bookings.length) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {done ? (
        <Alert severity="success" icon={<CheckCircle />} onClose={() => setDone(null)}>
          Cancelled {plural(done.cancelledCount, 'booking')}.
          {done.classesLeft != null ? ` ${plural(done.classesLeft, 'credit')} left now.` : ''}
          {done.skipped.length ? ` ${done.skipped.length} skipped: ${done.skipped[0].reason}.` : ''}
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <ToggleButtonGroup size="small" exclusive value={show} onChange={(_, v) => v && setShow(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75 } }}>
          <ToggleButton value="upcoming">Upcoming</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <Box display="flex" gap={0.5}>
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setMany(visible, true)}>Select all</Button>
          <Button size="small" color="inherit" sx={{ textTransform: 'none', color: 'text.secondary' }}
            onClick={() => setSelected(new Set())}>
            None
          </Button>
        </Box>
      </Box>

      {!visible.length ? (
        <Typography variant="body2" color="text.secondary">
          {show === 'upcoming' && bookings.length
            ? 'No upcoming bookings on this pack. Switch to All to see past ones.'
            : 'No bookings have been made with this pack.'}
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from(byMonth.entries()).map(([m, list]) => {
                const pickable = list.filter((b) => b.canCancel);
                const on = pickable.filter((b) => selected.has(b._id)).length;
                return (
                  <React.Fragment key={m}>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ bgcolor: 'grey.50' }}>
                        <Checkbox size="small" disabled={!pickable.length}
                          checked={pickable.length > 0 && on === pickable.length}
                          indeterminate={on > 0 && on < pickable.length}
                          onChange={() => setMany(list, on < pickable.length)} />
                      </TableCell>
                      <TableCell colSpan={4} sx={{ bgcolor: 'grey.50', fontWeight: 700, py: 0.75 }}>
                        {m === 'unknown' ? 'No date' : fmtMonth(m)}
                      </TableCell>
                    </TableRow>
                    {list.map((b) => {
                      // Already cancelled / checked in / no-show: shown for context, not pickable.
                      const blocked = !b.canCancel;
                      return (
                        <TableRow key={b._id} hover={!blocked} sx={{ opacity: blocked ? 0.6 : 1, cursor: blocked ? 'default' : 'pointer' }}
                          onClick={() => !blocked && toggle(b._id)}>
                          <TableCell padding="checkbox">
                            <Checkbox size="small" disabled={blocked} checked={selected.has(b._id)}
                              onClick={(e) => e.stopPropagation()} onChange={() => toggle(b._id)} />
                          </TableCell>
                          <TableCell>{b.istDate ? fmtDay(b.istDate) : '—'}</TableCell>
                          <TableCell>{b.istTime ? fmtTime(b.istTime) : '—'}</TableCell>
                          <TableCell>{b.title}</TableCell>
                          <TableCell><StatusTag status={b.bookingStatus} /></TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {picked.length ? (
        dry ? (
          <Alert severity={dry.cancelledCount === picked.length ? 'info' : 'warning'}>
            The server will cancel <b>{plural(dry.cancelledCount, 'booking')}</b> and give{' '}
            {dry.cancelledCount === 1 ? 'its class' : 'their classes'} back to the pack.
            {dry.skipped.length ? ` ${dry.skipped.length} will be skipped (${dry.skipped[0].reason}).` : ''}
          </Alert>
        ) : (
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
            <Typography variant="body2" fontWeight={700}>{plural(picked.length, 'booking')} selected</Typography>
            <Typography variant="body2" color="text.secondary">· +{plural(picked.length, 'credit')}</Typography>
          </Box>
        )
      ) : null}

      <Box display="flex" justifyContent="flex-end" gap={1}>
        {!dry ? (
          // Asks the server first (dry run); the confirmation above shows its answer.
          <Button variant="outlined" color="error" disabled={!picked.length || busy} onClick={() => run(true)}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <EventBusy />}>
            Cancel bookings
          </Button>
        ) : (
          <>
            <Button onClick={() => setDry(null)} disabled={busy}>Back</Button>
            <Button variant="contained" color="error" disabled={!dry.cancelledCount || busy} onClick={() => run(false)}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <EventBusy />}>
              {busy ? 'Cancelling…' : `Cancel ${plural(dry.cancelledCount, 'booking')}`}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PackBookingsCancelPane;
